import {createSlice} from "@reduxjs/toolkit";
import {
  fetchAttendanceHistory,
  fetchCurrentAttendance,
  punchIn,
  punchOut,
  fetchAttendanceByDateRange,
  updateAttendance,
  approveAttendance,
  getAttendanceByDate,
} from "../thunks/attendanceThunks";

const initialState = {
  currentAttendance: null,
  attendancehistory: [],
  loading: false,
  error: null,
  attendanceSummary: [],
  punchinSuccess: null,
  punchOutSuccess: null,
  attendanceByDate: null,
  // Whether the current user's role requires punch out (only Teachers do)
  requiresPunchOut: true,
};

const attendanceSlice = createSlice({
  name: "attendance",
  initialState,
  reducers: {
    clearAttendanceSummary: (state) => {
      state.attendanceSummary = [];
    },
    setCurrentAttendence: (state, action) => {
      state.currentAttendance = action?.payload;
    },
    setpunchinSuccess: (state, action) => {
      state.punchinSuccess = action.payload;
    },
    setpunchoutSuccess: (state, action) => {
      state.punchOutSuccess = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Current Attendance
      .addCase(fetchCurrentAttendance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentAttendance.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAttendance = action.payload?.data?.attendance;
        // Store whether the user's role requires punch out (only Teachers do)
        state.requiresPunchOut = action.payload?.data?.requiresPunchOut ?? true;
      })
      .addCase(fetchCurrentAttendance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Attendance History
      .addCase(fetchAttendanceHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAttendanceHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.attendancehistory = action.payload.data?.attendances || [];
      })
      .addCase(fetchAttendanceHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Punch In
      .addCase(punchIn.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.punchinSuccess = null;
      })
      .addCase(punchIn.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAttendance = action.payload?.data?.attendance;
        state.punchinSuccess = action?.payload?.message;
      })
      .addCase(punchIn.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message;
        state.punchinSuccess = null;
      })
      // Punch Out
      .addCase(punchOut.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.punchOutSuccess = null;
      })
      .addCase(punchOut.fulfilled, (state, action) => {
        state.loading = false;
        state.punchOutSuccess = action?.payload?.message;
        state.currentAttendance = null;
      })
      .addCase(punchOut.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.punchOutSuccess = null;
      })
      // Approve Attendance
      .addCase(approveAttendance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveAttendance.fulfilled, (state, action) => {
        state.loading = false;
        // Update the attendance record in the history
        const index = state.attendancehistory.findIndex(
          (record) =>
            record.date === action.payload.data.date &&
            record.userId === action.payload.data.userId
        );
        if (index !== -1) {
          state.attendancehistory[index] = {
            ...state.attendancehistory[index],
            approvalStatus: action.payload.data.approvalStatus,
            approvalComment: action.payload.data.approvalComment,
          };
        }
      })
      .addCase(approveAttendance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Attendance By Date Range
      .addCase(fetchAttendanceByDateRange.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAttendanceByDateRange.fulfilled, (state, action) => {
        state.loading = false;
        state.attendanceSummary = action.payload?.data?.attendanceSummary || [];
      })
      .addCase(fetchAttendanceByDateRange.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Attendance
      .addCase(updateAttendance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAttendance.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message;
      })
      .addCase(updateAttendance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Attendance
      .addCase(getAttendanceByDate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAttendanceByDate.fulfilled, (state, action) => {
        state.loading = false;
        state.attendanceByDate = action.payload.data;
      })
      .addCase(getAttendanceByDate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearAttendanceSummary,
  setCurrentAttendence,
  setpunchinSuccess,
  setpunchoutSuccess,
} = attendanceSlice.actions;
export default attendanceSlice.reducer;
